// Adds/removes/reorders qualification question rows in the webinar
// registration form builder. Mirrors webinar_checklists_controller, capped at 5.
// Attach/create posts are submitted via temporary forms so they are not nested
// inside the parent webinar form (invalid HTML).
import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["fieldTemplate", "fields", "newQuestionPanel", "addDropdown"];
  static values = {
    attachUrl: String,
    createAttachUrl: String
  };

  MAX_FIELDS = 5;

  connect() {
    this.reindexPositions();
  }

  addField(event) {
    event.preventDefault();
    if (!this.canAddMore()) return;

    this.hideNewQuestionPanel();
    const newField = this.fieldTemplateTarget.content.cloneNode(true);
    const uniqueId = new Date().getTime();
    const html = newField.firstElementChild.innerHTML.replace(
      /NEW_RECORD/g,
      uniqueId
    );
    newField.firstElementChild.innerHTML = html;
    this.fieldsTarget.appendChild(newField);
    this.reindexPositions();
  }

  showNewQuestionPanel(event) {
    event.preventDefault();
    if (!this.canAddMore()) return;

    if (this.hasNewQuestionPanelTarget) {
      this.newQuestionPanelTarget.classList.remove("d-none");
      const firstInput = this.newQuestionPanelTarget.querySelector(
        "input[type='text'], textarea"
      );
      if (firstInput) firstInput.focus();
    } else {
      this.addField(event);
    }
  }

  hideNewQuestionPanel(event) {
    if (event) event.preventDefault();
    if (this.hasNewQuestionPanelTarget) {
      this.newQuestionPanelTarget.classList.add("d-none");
    }
  }

  attachQuestion(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.canAddMore()) return;

    const questionId = event.currentTarget.dataset.questionId;
    if (!questionId || !this.attachUrlValue) return;

    this.submitExternalPost(this.attachUrlValue, {
      qualification_question_id: questionId
    });
  }

  createAndAttach(event) {
    event.preventDefault();
    if (!this.canAddMore()) return;
    if (!this.hasNewQuestionPanelTarget || !this.createAttachUrlValue) return;

    const panel = this.newQuestionPanelTarget;
    const label = panel.querySelector("[data-new-question-label]")?.value?.trim();
    const optionInputs = Array.from(
      panel.querySelectorAll("[data-new-question-option]")
    );
    const options = optionInputs.map((input) => input.value.trim()).filter(Boolean);

    if (!label) {
      alert("Please enter a question.");
      return;
    }
    if (options.length < 2) {
      alert("Please enter at least 2 options.");
      return;
    }

    this.submitExternalPost(this.createAttachUrlValue, {
      "qualification_question[label]": label,
      "qualification_question[options][]": options
    });
  }

  removeField(event) {
    event.preventDefault();
    const field = event.target.closest(".registration-form-field-fields");
    const removeControl = event.currentTarget;
    const followUpCount = parseInt(field?.dataset.followUpCount || "0", 10);
    const hasFollowUps =
      followUpCount > 0 || removeControl?.dataset?.hasFollowUps === "true";

    if (hasFollowUps) {
      alert(
        "Remove or reassign follow-up questions on this webinar before removing this parent question."
      );
      return;
    }

    if (field.querySelector('input[name*="_destroy"]')) {
      field.querySelector('input[name*="_destroy"]').value = "1";
      field.style.display = "none";
      field.querySelectorAll("input, textarea, select").forEach((input) => {
        input.required = false;
      });
    } else {
      field.remove();
    }
    this.reindexPositions();
  }

  moveUp(event) {
    event.preventDefault();
    const field = event.target.closest(".registration-form-field-fields");
    const prev = this.previousVisibleField(field);
    if (prev) {
      this.fieldsTarget.insertBefore(field, prev);
      this.reindexPositions();
    }
  }

  moveDown(event) {
    event.preventDefault();
    const field = event.target.closest(".registration-form-field-fields");
    const next = this.nextVisibleField(field);
    if (next) {
      this.fieldsTarget.insertBefore(next, field);
      this.reindexPositions();
    }
  }

  canAddMore() {
    // Cap includes follow-ups nested under direct rows.
    const visibleCount = this.totalQuestionCount();
    if (visibleCount >= this.MAX_FIELDS) {
      alert(`You can only add a maximum of ${this.MAX_FIELDS} questions.`);
      return false;
    }
    return true;
  }

  totalQuestionCount() {
    return this.visibleFields().reduce((sum, field) => {
      const followUps = parseInt(field.dataset.followUpCount || "0", 10);
      return sum + 1 + (Number.isNaN(followUps) ? 0 : followUps);
    }, 0);
  }

  visibleFields() {
    return Array.from(
      this.fieldsTarget.querySelectorAll(".registration-form-field-fields")
    ).filter((field) => field.style.display !== "none");
  }

  previousVisibleField(field) {
    let el = field.previousElementSibling;
    while (el) {
      if (
        el.classList.contains("registration-form-field-fields") &&
        el.style.display !== "none"
      ) {
        return el;
      }
      el = el.previousElementSibling;
    }
    return null;
  }

  nextVisibleField(field) {
    let el = field.nextElementSibling;
    while (el) {
      if (
        el.classList.contains("registration-form-field-fields") &&
        el.style.display !== "none"
      ) {
        return el;
      }
      el = el.nextElementSibling;
    }
    return null;
  }

  reindexPositions() {
    let index = 0;
    this.visibleFields().forEach((field) => {
      const positionInput = field.querySelector(
        'input[name*="[position]"]:not([data-follow-up-position])'
      );
      if (positionInput) {
        positionInput.value = index;
        index += 1;
      }
      field.querySelectorAll("input[data-follow-up-position]").forEach((input) => {
        input.value = index;
        index += 1;
      });
    });
  }

  submitExternalPost(url, params) {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = url;
    form.style.display = "none";

    const csrf = document.querySelector("meta[name='csrf-token']")?.content;
    if (csrf) {
      this.appendHidden(form, "authenticity_token", csrf);
    }

    Object.entries(params).forEach(([name, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => this.appendHidden(form, name, item));
      } else {
        this.appendHidden(form, name, value);
      }
    });

    document.body.appendChild(form);
    form.submit();
  }

  appendHidden(form, name, value) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
}
